"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { LogIn, UserPlus, Lock } from "lucide-react"
import Link from "next/link"

interface AuthPopupProps {
  isOpen: boolean
  /** Called with open state; closes when called with false. */
  onOpenChange?: (open: boolean) => void
  onClose: () => void
  action?: string
  /** Appended as `redirect` query on Sign In so the user returns after login. Must be path-only (e.g. `/meme-generator/drake`). */
  redirectTo?: string | null
}

export default function AuthPopup({
  isOpen,
  onOpenChange,
  onClose,
  action = "perform this action",
  redirectTo,
}: AuthPopupProps) {
  const loginHref =
    redirectTo &&
    redirectTo.startsWith('/') &&
    !redirectTo.startsWith('//')
      ? `/login?redirect=${encodeURIComponent(redirectTo)}`
      : '/login'

  const handleDialogOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    if (!open) onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-md bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="text-white text-xl font-semibold flex items-center gap-2">
            <Lock className="h-6 w-6 text-red-500" />
            Authentication Required
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-6 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
              <LogIn className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-white text-lg font-medium mb-2">
              Login Required
            </h3>
            <p className="text-zinc-400 text-sm">
              You need to be logged in to {action}.
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Link href={loginHref} className="flex-1">
              <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Button>
            </Link>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-zinc-500 text-sm">
              Don&apos;t have an account?{" "}
              <Link href="/register" className="text-red-400 hover:text-red-300 underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
} 
